import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Plus, MapPin, Tag, Trash2, Building, MoreHorizontal, Pencil, UserX, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const AdminGuides = () => {
  const [guides, setGuides] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [monuments, setMonuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  
  const [assignForm, setAssignForm] = useState({
    guide_user_id: '',
    city_id: '',
    category_id: '',
    monument_id: '',
    is_active: true,
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, citiesRes, categoriesRes, monumentsRes, assignmentsRes] = await Promise.all([
        adminAPI.listUsers({ role: 'guide' }),
        adminAPI.listCities(),
        adminAPI.listCategories(),
        adminAPI.listMonuments(),
        adminAPI.listGuideCityCategories(),
      ]);
      setGuides(usersRes.data);
      setCities(citiesRes.data);
      setCategories(categoriesRes.data);
      setMonuments(monumentsRes.data);
      setAssignments(assignmentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      // Only send non-empty values
      const data = {
        guide_user_id: assignForm.guide_user_id,
        is_active: assignForm.is_active,
      };
      if (assignForm.city_id) data.city_id = assignForm.city_id;
      if (assignForm.category_id) data.category_id = assignForm.category_id;
      if (assignForm.monument_id) data.monument_id = assignForm.monument_id;
      
      await adminAPI.assignGuideCityCategory(data);
      toast.success('Guide assigned successfully');
      setAssignDialogOpen(false);
      setAssignForm({ guide_user_id: '', city_id: '', category_id: '', monument_id: '', is_active: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign guide');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await adminAPI.deleteGuideCityCategory(assignmentId);
      toast.success('Assignment removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const openAssignDialog = (guide) => {
    setSelectedGuide(guide);
    setAssignForm({ ...assignForm, guide_user_id: guide.id, city_id: '', category_id: '', monument_id: '' });
    setAssignDialogOpen(true);
  };

  const openEditDialog = (guide) => {
    setSelectedGuide(guide);
    setEditForm({
      name: guide.name || '',
      email: guide.email || '',
      phone: guide.phone || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditGuide = async (e) => {
    e.preventDefault();
    if (!selectedGuide) return;
    
    try {
      await adminAPI.updateUser(selectedGuide.id, {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone || null,
      });
      toast.success('Guide updated successfully');
      setEditDialogOpen(false);
      setSelectedGuide(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guide');
    }
  };

  const handleToggleStatus = async (guide) => {
    try {
      const newStatus = guide.status === 'active' ? 'disabled' : 'active';
      await adminAPI.updateUser(guide.id, { status: newStatus });
      toast.success(`Guide ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update guide status');
    }
  };

  const getGuideAssignments = (guideId) => {
    return assignments.filter(a => a.guide_user_id === guideId);
  };

  const getAssignmentLabel = (a) => {
    const parts = [];
    if (a.city_name) parts.push(a.city_name);
    if (a.category_name) parts.push(a.category_name);
    if (a.monument_name) parts.push(a.monument_name);
    return parts.join(' / ') || 'No details';
  };

  return (
    <AdminLayout title="Guides" subtitle="Manage guide profiles and city/category assignments">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Guide Directory</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guide</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assignments</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {guides.map((guide) => {
                    const guideAssignments = getGuideAssignments(guide.id);
                    return (
                      <TableRow key={guide.id} className="table-row-hover" data-testid={`guide-row-${guide.id}`}>
                        <TableCell className="font-medium">{guide.name}</TableCell>
                        <TableCell>{guide.email}</TableCell>
                        <TableCell>
                          <Badge className={guide.status === 'active' ? 'badge-success' : 'badge-neutral'}>
                            {guide.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {guideAssignments.slice(0, 3).map(a => (
                              <Badge key={a.id} variant="outline" className="text-xs">
                                {getAssignmentLabel(a)}
                              </Badge>
                            ))}
                            {guideAssignments.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{guideAssignments.length - 3} more
                              </Badge>
                            )}
                            {guideAssignments.length === 0 && (
                              <span className="text-sm text-slate-400">No assignments</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openAssignDialog(guide)}
                              data-testid={`assign-btn-${guide.id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Assign
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`guide-actions-${guide.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(guide)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleToggleStatus(guide)}>
                                  {guide.status === 'active' ? (
                                    <>
                                      <UserX className="w-4 h-4 mr-2" />
                                      Disable
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4 mr-2" />
                                      Enable
                                    </>
                                  )}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {guides.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No guides found. Create guide users from the Users page first.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* All Assignments */}
        <Card>
          <CardHeader>
            <CardTitle>All Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guide</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Monument</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((assignment) => {
                  const guide = guides.find(g => g.id === assignment.guide_user_id);
                  return (
                    <TableRow key={assignment.id} data-testid={`assignment-row-${assignment.id}`}>
                      <TableCell className="font-medium">{guide?.name || 'Unknown'}</TableCell>
                      <TableCell>
                        {assignment.city_name ? (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {assignment.city_name}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {assignment.category_name ? (
                          <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4 text-slate-400" />
                            {assignment.category_name}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {assignment.monument_name ? (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-slate-400" />
                            {assignment.monument_name}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={assignment.is_active ? 'badge-success' : 'badge-neutral'}>
                          {assignment.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteAssignment(assignment.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {assignments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No assignments yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Guide</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4 mt-4">
            {selectedGuide && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedGuide.name}</p>
                <p className="text-sm text-slate-500">{selectedGuide.email}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>City (optional)</Label>
              <Select 
                value={assignForm.city_id || 'none'} 
                onValueChange={(v) => setAssignForm({ ...assignForm, city_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="assign-city-select">
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {cities.map(city => (
                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Category (optional)</Label>
              <Select 
                value={assignForm.category_id || 'none'} 
                onValueChange={(v) => setAssignForm({ ...assignForm, category_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="assign-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Monument (optional)</Label>
              <Select 
                value={assignForm.monument_id || 'none'} 
                onValueChange={(v) => setAssignForm({ ...assignForm, monument_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="assign-monument-select">
                  <SelectValue placeholder="Select monument" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {monuments.map(mon => (
                    <SelectItem key={mon.id} value={mon.id}>{mon.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch 
                checked={assignForm.is_active}
                onCheckedChange={(v) => setAssignForm({ ...assignForm, is_active: v })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-assign-btn">
                Assign
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Guide Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Guide</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditGuide} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                data-testid="edit-guide-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
                data-testid="edit-guide-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+1234567890"
                data-testid="edit-guide-phone-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-edit-guide-btn">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminGuides;
